import { flatten, get, isNil } from 'lodash';
import { Optional } from 'utility-types';
import { PrefilledData, Question as QuestionInterface, QUESTION_TYPES, ValidationRules, FlattenQuestionSurveyProps } from '@commutifi-fe/interfaces';
import { Form, FormInstance, MarkdownWrapper, Question } from '@commutifi-fe/ui';
import { QuestionProps } from '@commutifi/models/Question';
import { computeEditability } from '@commutifi/utils/surveyQuestionEditability';
import { COMPONENT_TYPES } from 'constants/surveys';
import { AddressPoint, BuildingGeocodeValue } from 'shared/interfaces';
import { CumulatedAnswers } from 'shared/interfaces/Survey';
import { REF_KEYS_LIST } from 'constants/onboarding';

/**
 * Question type is specific to our survey that commuters take. Those questions have a
 * single localized content and have some specially processed data.
 *
 * @param question - the question object
 * @returns the question reference key or id (As we consider ref keys an id on the commuter survey)
 */
export const getQuestionId = (question: QuestionInterface) => question.referenceKey || question.id;

/**
 * Returns the building answer from the cumulated answers.
 */
export const getBuildingAnswer = (answers: CumulatedAnswers): BuildingGeocodeValue | undefined => answers[REF_KEYS_LIST.accountBuildingId];
export const getBuildingGeolocation = (prefilledData: PrefilledData = {}, cumulatedAnswers: CumulatedAnswers = {}): BuildingGeocodeValue | undefined => {
  const buildingAnswer = cumulatedAnswers[REF_KEYS_LIST.accountBuildingId];
  const prefilledBuilding = prefilledData.building ? {
    formattedAddress: prefilledData.building.address,
    location: prefilledData.building.location,
    value: prefilledData.building.id
  } : buildingAnswer;
  return prefilledBuilding || undefined;
};
export const getHomeGeolocation = (cumulatedAnswers: CumulatedAnswers = {}): AddressPoint | undefined => cumulatedAnswers[REF_KEYS_LIST.accountAddress];
export const getWorksiteId = (prefilledData: PrefilledData = {}, cumulatedAnswers: CumulatedAnswers = {}): string | undefined => prefilledData.worksite?.id || prefilledData.accountContext?.worksiteId as string | undefined || cumulatedAnswers[REF_KEYS_LIST.accountWorksiteId] as string | undefined;

/**
 * antd field names are like this ['parentProp', 'nested', etc.]
 * This function is just doing that formatting from an id like this: 'parentProp.nested.subProp'
 * @param question - the question object
 * @param subQuestionId - the id of the sub question
 * @returns the formatted id
 */
export const generateSubQuestionId = (question: QuestionInterface, subQuestionId: string) => {
  if (!question?.id) {
    return '';
  }
  return flatten([getQuestionId(question), (subQuestionId || '').split('.')]);
};
export const isQuestionBefore = (q1: Optional<Pick<QuestionInterface, 'section' | 'order'>>, q2: Optional<Pick<QuestionInterface, 'section' | 'order'>>, orEqual: boolean = false) => !isNil(q1.section) && !isNil(q2.section) && (q1.section < q2.section || q1.section === q2.section && !isNil(q1.order) && !isNil(q2.order) && (orEqual ? q1.order <= q2.order : q1.order < q2.order));
export const isQuestionPositionEqual = (q1: Optional<Pick<QuestionInterface, 'section' | 'order'>>, q2: Optional<Pick<QuestionInterface, 'section' | 'order'>>) => !isNil(q1.section) && !isNil(q2.section) && q1.section === q2.section && q1.order === q2.order;

/**
 * Question classification is delegated to the shared editability contract
 * (`@commutifi/utils/surveyQuestionEditability`), which reads the same core-question
 * catalog (`@commutifi/constants/Surveys`) the backend enforces — the frontend
 * cannot drift from the backend's rules.
 * @param question - the question object
 * @returns true if the question is a core question, false otherwise
 */
export const getIsCoreQuestion = (questionOrQuestionSurvey: QuestionInterface | QuestionProps | FlattenQuestionSurveyProps): boolean => {
  const {
    type,
    referenceKey
  } = questionOrQuestionSurvey;
  // An array reference key (sub-questions) can never match a core catalog entry
  if (!type || Array.isArray(referenceKey)) {
    return false;
  }
  return computeEditability(type, referenceKey ?? null).isCore;
};
export const getIsDeletableCoreQuestion = (questionOrQuestionSurvey: QuestionInterface | QuestionProps | FlattenQuestionSurveyProps): boolean => {
  const {
    type,
    referenceKey
  } = questionOrQuestionSurvey;
  if (!type || Array.isArray(referenceKey)) {
    return false;
  }
  const {
    isCore,
    isDeletable
  } = computeEditability(type, referenceKey ?? null);
  return isCore && isDeletable;
};
export const getIsDeletableQuestion = (questionOrQuestionSurvey: QuestionInterface | QuestionProps | FlattenQuestionSurveyProps): boolean => {
  const {
    type,
    referenceKey
  } = questionOrQuestionSurvey;
  // A question outside the core catalog (including array reference keys) is always deletable
  if (!type || Array.isArray(referenceKey)) {
    return true;
  }
  return computeEditability(type, referenceKey ?? null).isDeletable;
};
export const renderQuestion = (questionProps: {
  validation?: ValidationRules;
  question: QuestionInterface | FlattenQuestionSurveyProps;
  fieldName: string | string[];
  props: Partial<any>;
  component: React.ComponentType<any>;
  componentType: COMPONENT_TYPES;
  defaultValue?: any;
  form?: FormInstance;
  upsertQuestions?: (questions: QuestionInterface[]) => void;
}) => {
  const {
    question,
    validation,
    fieldName,
    props,
    component: Component,
    componentType,
    form,
    upsertQuestions
  } = questionProps;
  return componentType === COMPONENT_TYPES.Module ? <Component key={question.id} question={question} form={form} upsertQuestions={upsertQuestions} validation={validation} {...props} /> : question.type === QUESTION_TYPES.statement ? <Question key={question.id} title={typeof get(question, 'content.title') === 'string' ? <MarkdownWrapper source={get(question, 'content.title', '') as string} /> : get(question, 'content.title')} hint={typeof get(question, 'content.hint') === 'string' ? <MarkdownWrapper source={get(question, 'content.hint', '')} /> : get(question, 'content.hint')} data-testid={fieldName} /> : <Form.Item name={fieldName} key={question.id} data-testid={fieldName} label={<Question title={get(question, 'content.title')} hint={get(question, 'content.hint')} />} required={!question.optional} {...validation}>
      <Component {...props} form={form} />
    </Form.Item>;
};